/**
 * AI Chat API Client
 *
 * Pure API functions for communicating with the AI Chat backend.
 * No React hooks — just async fetch functions.
 */

import {
  AIChatMessage,
  SendMessageInput,
  SendMessageResponse,
  ChatHistoryResponse,
  ActionConfirmationInput,
  ActionConfirmationResponse,
} from '../types/ai-chat.types';
import { refreshTokenCall, isTokenError } from '../API/APIs/APIv1';
import type { ConversationsListResponse } from './ai-chat-store';

// API base URL
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// ─────────────────────────────────────────────────────────────────────────────
// Auth helpers
// ─────────────────────────────────────────────────────────────────────────────

function getAuthHeaders(includeContentType = true): HeadersInit {
  const token = localStorage.getItem('user-token');
  const parsedToken = token ? JSON.parse(token) : null;

  if (!parsedToken?.accessToken) {
    throw new Error('No access token available');
  }

  const headers: HeadersInit = {
    Authorization: `Bearer ${parsedToken.accessToken}`,
  };

  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
}

/**
 * Fetch wrapper with automatic 401 handling and token refresh
 */
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
  includeContentType = true
): Promise<Response> {
  const headers = { ...getAuthHeaders(includeContentType), ...options.headers };
  let response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    const errorBody = await response.json().catch(() => ({ message: 'Unauthorized' }));
    const errorMessage = errorBody.message || 'Unauthorized';

    if (isTokenError(errorMessage)) {
      const refreshResult = await refreshTokenCall();

      if (refreshResult && !refreshResult.error && refreshResult.accessToken) {
        const newHeaders = {
          ...options.headers,
          Authorization: `Bearer ${refreshResult.accessToken}`,
          ...(includeContentType ? { 'Content-Type': 'application/json' } : {}),
        };
        response = await fetch(url, { ...options, headers: newHeaders });
      } else {
        throw new Error('Session expired. Please log in again.');
      }
    } else {
      throw new Error(errorMessage);
    }
  }

  return response;
}

// ─────────────────────────────────────────────────────────────────────────────
// Conversation CRUD
// ─────────────────────────────────────────────────────────────────────────────

export async function listConversations(limit = 20, offset = 0): Promise<ConversationsListResponse> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const response = await fetchWithAuth(`${API_BASE_URL}/ai-chat/conversations?${params}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch conversations' }));
    throw new Error(error.message || 'Failed to fetch conversations');
  }

  const data = await response.json();

  if (data.conversations) {
    data.conversations = data.conversations.map((conv: any) => ({
      ...conv,
      updatedAt: new Date(conv.updatedAt),
    }));
  }

  return data;
}

export async function createConversation(title?: string): Promise<{ id: string; title: string; createdAt: Date }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/ai-chat/conversations`, {
    method: 'POST',
    body: JSON.stringify({ title }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to create conversation' }));
    throw new Error(error.message || 'Failed to create conversation');
  }

  const data = await response.json();
  return { ...data, createdAt: new Date(data.createdAt) };
}

export async function renameConversation(conversationId: string, title: string): Promise<{ id: string; title: string }> {
  return updateConversation(conversationId, { title });
}

export interface ConversationUpdateFields {
  title?: string;
  /** null entfernt die Konversation aus ihrem Ordner */
  folder?: string | null;
  isArchived?: boolean;
}

export async function updateConversation(
  conversationId: string,
  fields: ConversationUpdateFields
): Promise<{ id: string; title: string; folder: string | null; isArchived: boolean }> {
  const response = await fetchWithAuth(
    `${API_BASE_URL}/ai-chat/conversations/${conversationId}`,
    { method: 'PATCH', body: JSON.stringify(fields) }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to update conversation' }));
    throw new Error(error.message || 'Failed to update conversation');
  }

  return response.json();
}

export async function renameFolder(from: string, to: string): Promise<{ moved: number }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/ai-chat/folders/rename`, {
    method: 'POST',
    body: JSON.stringify({ from, to }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to rename folder' }));
    throw new Error(error.message || 'Failed to rename folder');
  }

  return response.json();
}

export async function deleteConversation(conversationId: string): Promise<void> {
  const response = await fetchWithAuth(
    `${API_BASE_URL}/ai-chat/conversations/${conversationId}`,
    { method: 'DELETE' },
    false
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to delete conversation' }));
    throw new Error(error.message || 'Failed to delete conversation');
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Messages
// ─────────────────────────────────────────────────────────────────────────────

export async function sendMessage(input: SendMessageInput & { conversationId?: string }): Promise<SendMessageResponse> {
  const response = await fetchWithAuth(`${API_BASE_URL}/ai-chat/message`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to send message' }));
    throw new Error(error.message || 'Failed to send message');
  }

  const data = await response.json();
  if (data.message?.timestamp) {
    data.message.timestamp = new Date(data.message.timestamp);
  }
  return data;
}

export async function getChatHistory(
  limit = 50,
  offset = 0,
  conversationId?: string
): Promise<ChatHistoryResponse & { conversationId?: string }> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (conversationId) {
    params.set('conversationId', conversationId);
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/ai-chat/history?${params}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch history' }));
    throw new Error(error.message || 'Failed to fetch history');
  }

  const data = await response.json();
  if (data.messages) {
    data.messages = data.messages.map((msg: AIChatMessage) => ({
      ...msg,
      timestamp: new Date(msg.timestamp),
    }));
  }
  return data;
}

export async function clearChatHistory(conversationId?: string): Promise<void> {
  const params = conversationId ? new URLSearchParams({ conversationId }) : '';
  const response = await fetchWithAuth(
    `${API_BASE_URL}/ai-chat/history${params ? `?${params}` : ''}`,
    { method: 'DELETE' },
    false
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to clear history' }));
    throw new Error(error.message || 'Failed to clear history');
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Actions
// ─────────────────────────────────────────────────────────────────────────────

export async function confirmAction(input: ActionConfirmationInput): Promise<ActionConfirmationResponse> {
  const response = await fetchWithAuth(`${API_BASE_URL}/ai-chat/action/confirm`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to confirm action' }));
    throw new Error(error.message || 'Failed to confirm action');
  }

  const data = await response.json();
  if (data.message?.timestamp) {
    data.message.timestamp = new Date(data.message.timestamp);
  }
  return data;
}

export async function cancelAction(actionId: string): Promise<ActionConfirmationResponse> {
  const response = await fetchWithAuth(`${API_BASE_URL}/ai-chat/action/cancel`, {
    method: 'POST',
    body: JSON.stringify({ actionId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to cancel action' }));
    throw new Error(error.message || 'Failed to cancel action');
  }

  const data = await response.json();
  if (data.message?.timestamp) {
    data.message.timestamp = new Date(data.message.timestamp);
  }
  return data;
}
